import tokens from '@kluein/design-tokens';
import { Box, Button, Text, CheckBox, Grid } from '@kluein/klue-ui';
import { CheckCircle, Sparkle, WarningCircle } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { HorizontalScroller } from 'components/layout/scroller/horizontal-scroller';
import { useAuth } from 'contexts/auth';
import useAskKlueLog from 'hooks/dashboard/use-ask-klue-log/useAskKlueLog';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { useResponsive } from 'hooks/use-responsive';
import { AnalyticsAction } from 'lib/analytics';
import { useAskKlue } from 'pages/dashboard/hooks';
import {
  setAskKlueInitialScreen,
  setAskKlueWizardId,
} from 'store/views/dashboard';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import {
  AskKlueModal,
  AskKlueInput,
  AskKlueSingleClickPrompt,
} from './partials';
import { askKlueWizardFlow } from './partials/ask-klue-modal-wizard/data/askKlueWizardFlow';

import type { Dispatch } from 'store/store.types';

const FADE_GRADIENT = 'rgba(248, 246, 247, 1) 10%, rgba(255, 255, 255, 0) 100%';
const SCROLL_GAP = 500;

function AskKlue({
  question,
  shouldShowModal,
}: {
  question?: string;
  shouldShowModal?: boolean;
}) {
  const dispatch = useDispatch<Dispatch>();
  const { notify } = useNotifier();
  const { maxWidth } = useResponsive();
  const { t } = useTranslation(['Dashboard', 'Common']);
  const {
    isAskKlueWizardEnabled,
    isDashboardAskKluePreviewForCuratorsEnabled,
    isConsumerAskKlueToggleHidden,
    isDashboardAskKlueEnabled,
    isCurator,
    isAdmin,
    isUnifiedSearchEnabled,
  } = useAuth();
  const {
    showModal,
    visibilityGroupId,
    contextPage,
    openModal,
    handleAskKlue,
    verifyAskKlueData,
    clearAskKlueSession,
  } = useAskKlue();

  const logAskKlueEvent = useAskKlueLog();

  const isConsumerToggleVisible =
    !isConsumerAskKlueToggleHidden &&
    isDashboardAskKluePreviewForCuratorsEnabled &&
    (isCurator || isAdmin);

  const handleCheckBox = (checked: boolean) => {
    try {
      dispatch.auth.updateFeatureFlag({
        path: 'isDashboardAskKlueDisabled',
        value: !checked,
      });

      notify({
        type: NotifierType.SUCCESS,
        message: t(
          `Dashboard:askKlue.toggleFeedback.${
            checked ? 'enabled' : 'disabled'
          }`,
        ),
        icon: <CheckCircle />,
      });
    } catch (error) {
      return notify({
        type: NotifierType.ERROR,
        message: t('Common:fatalErrorFeedback.title'),
        icon: <WarningCircle />,
      });
    }
  };

  useEffect(() => {
    clearAskKlueSession();
    verifyAskKlueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (question && shouldShowModal) {
      openModal();
      handleAskKlue(question);
    }

    logAskKlueEvent({
      askKlueId: '',
      eventType: AnalyticsAction.renderAskKlue,
      page: contextPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (visibilityGroupId !== undefined) {
      openModal('chat');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityGroupId]);

  if (isUnifiedSearchEnabled) {
    return null;
  }

  return (
    <>
      <Grid
        rows={maxWidth.medium ? ['1fr', 'auto'] : ['auto']}
        columns={maxWidth.medium ? ['1fr'] : ['auto', '1fr']}
        align="center"
        gap="large"
        data-test-id={TEST_IDS.askKlue.container}
      >
        <AskKlueInput
          handleAskKlue={handleAskKlue}
          isClickableButton={true}
          handleClickableButton={() => {
            openModal('chat');
            setAskKlueInitialScreen(true);
          }}
        />
        <Grid
          rows={['auto']}
          columns={isConsumerToggleVisible ? ['1fr', '130px'] : ['1fr']}
          align="center"
          gap="small"
        >
          <HorizontalScroller
            gradient={FADE_GRADIENT}
            enableControl={true}
            scrollGap={SCROLL_GAP}
          >
            <Box align="center" justify="start" direction="row" gap="small">
              {isAskKlueWizardEnabled && (
                <Box
                  gap="small"
                  direction="row"
                  align="center"
                  flex={{ grow: 1 }}
                >
                  {askKlueWizardFlow.map((item) => (
                    <Button
                      leftSlot={<Sparkle />}
                      variant="secondary-form"
                      label={item.title}
                      onClick={() => {
                        setAskKlueWizardId(item.id);
                        openModal('chat');
                      }}
                      data-tracking-id={TRACKING_IDS.askKlue.wizardButton}
                    />
                  ))}
                </Box>
              )}
              <AskKlueSingleClickPrompt handleAskKlue={handleAskKlue} />
            </Box>
          </HorizontalScroller>
          {isConsumerToggleVisible && (
            <Box align="center" direction="row" gap="small">
              <CheckBox
                data-test-id={TEST_IDS.askKlue.dashboardConsumerToggle}
                data-tracking-id={TRACKING_IDS.askKlue.dashboardConsumerToggle}
                toggle={true}
                a11yTitle={t('Dashboard:askKlue.toggleLabel')}
                gap="small"
                checked={isDashboardAskKlueEnabled}
                onChange={(event) => handleCheckBox(event.target.checked)}
              />
              <Text
                size="small"
                color={tokens.color.primitives.gray[900]}
                fontFamily={tokens.fontFamily.Inter}
              >
                {t('Dashboard:askKlue.toggleLabel')}
              </Text>
            </Box>
          )}
        </Grid>
      </Grid>
      {/* if isUnifiedSearchEnabled then the display of <AskKlueModal is handled in App.tsx by
      {showModal && isUnifiedSearchEnabled && <AskKlueModal />} */}
      {showModal && <AskKlueModal />}
    </>
  );
}

export default AskKlue;
